import { useMemo, useState } from 'react';
import { PauseCircle, PlayCircle, Search, Trash2, Users } from 'lucide-react';
import clsx from 'clsx';
import type { AdminUserRow } from '@nlinks/shared';
import { ApiError } from '@/lib/api';
import { useAdminActions, useAdminUsers } from '@/lib/queries';
import { formatNumber, formatRelative } from '@/lib/format';
import { Pagination } from '@/components/Pagination';
import { InfoButton } from '@/components/InfoButton';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Modal,
  Select,
  TableSkeleton,
  humanizeStatus,
  statusTone,
  useToast,
} from '@/components/ui';

export function ViewUsersPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [confirm, setConfirm] = useState<{ user: AdminUserRow; action: 'hold' | 'delete' } | null>(null);

  const { push } = useToast();
  const { toggleHold, deleteUser } = useAdminActions();

  const params = useMemo(
    () => ({ page, pageSize: 25, search: search || undefined, status: status || undefined }),
    [page, search, status],
  );
  const { data, isLoading, isPlaceholderData } = useAdminUsers(params);

  const act = async () => {
    if (!confirm) return;
    const { user, action } = confirm;
    try {
      if (action === 'delete') {
        await deleteUser.mutateAsync(user.id);
        push('success', `${user.fullName} removed.`);
      } else {
        const result = await toggleHold.mutateAsync(user.id);
        push(
          'success',
          result.status === 'ON_HOLD'
            ? `${user.fullName} is on hold — sign-in blocked and mailbox monitoring paused.`
            : `${user.fullName} released. Monitoring resumes on the next sync.`,
        );
      }
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'That action failed.');
    } finally {
      setConfirm(null);
    }
  };

  const resetAnd = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-slate-100">Users</h1>
        <p className="mt-1 text-sm text-slate-500">
          {data ? `${formatNumber(data.total)} account${data.total === 1 ? '' : 's'}` : 'Loading…'}
        </p>
      </div>

      <Card>
        <div className="flex flex-wrap items-center gap-3 border-b border-surface-border p-4">
          <div className="relative min-w-[16rem] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600" aria-hidden />
            <Input
              value={search}
              onChange={(e) => resetAnd(setSearch)(e.target.value)}
              placeholder="Search name or email…"
              className="pl-9"
            />
          </div>
          <Select value={status} onChange={(e) => resetAnd(setStatus)(e.target.value)} className="w-auto">
            <option value="">All statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="ON_HOLD">On hold</option>
            <option value="PENDING_ACTIVATION">Never signed in</option>
          </Select>
        </div>

        {isLoading ? (
          <TableSkeleton rows={8} columns={6} />
        ) : data?.items.length ? (
          <>
            <div className={clsx('overflow-x-auto transition-opacity', isPlaceholderData && 'opacity-60')}>
              <table className="table-base">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>
                      <span className="inline-flex items-center gap-1.5">
                        Status
                        <InfoButton label="Status">
                          On hold blocks sign-in and pauses every mailbox they own — no mail is read and no links are
                          opened until you release them.
                        </InfoButton>
                      </span>
                    </th>
                    <th className="text-center">
                      <span className="inline-flex items-center gap-1.5">
                        Mailboxes
                        <InfoButton label="Mailboxes">
                          Actively syncing, out of the total they have connected.
                        </InfoButton>
                      </span>
                    </th>
                    <th className="text-right">
                      <span className="inline-flex items-center justify-end gap-1.5">
                        Mails matched
                        <InfoButton label="Mails matched" align="right">
                          Messages matching their keywords. Mail that matched nothing is counted for their daily
                          totals and discarded.
                        </InfoButton>
                      </span>
                    </th>
                    <th className="text-right">
                      <span className="inline-flex items-center justify-end gap-1.5">
                        Mails opened
                        <InfoButton label="Mails opened" align="right">
                          Their matched mail with at least one link that was reached. Counted once per mail, however many links it contained.
                        </InfoButton>
                      </span>
                    </th>
                    <th className="text-right">Last sign-in</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <p className="font-medium text-slate-200">{user.fullName}</p>
                        <p className="truncate text-xs text-slate-500">{user.email}</p>
                      </td>
                      <td>
                        <Badge tone={statusTone(user.status)}>{humanizeStatus(user.status)}</Badge>
                      </td>
                      <td className="text-center">
                        <Badge tone={user.activeMailAccountCount > 0 ? 'success' : 'neutral'}>
                          {user.activeMailAccountCount}/{user.mailAccountCount}
                        </Badge>
                      </td>
                      <td className="text-right tabular-nums text-slate-300">{formatNumber(user.totals.messages)}</td>
                      <td className="text-right tabular-nums text-emerald-400">
                        {formatNumber(user.totals.mailsOpened)}
                      </td>
                      <td className="whitespace-nowrap text-right text-xs text-slate-500">
                        {formatRelative(user.lastLoginAt)}
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setConfirm({ user, action: 'hold' })}
                            icon={
                              user.status === 'ON_HOLD' ? (
                                <PlayCircle className="h-3.5 w-3.5" />
                              ) : (
                                <PauseCircle className="h-3.5 w-3.5" />
                              )
                            }
                          >
                            {user.status === 'ON_HOLD' ? 'Release' : 'Hold'}
                          </Button>
                          <button
                            onClick={() => setConfirm({ user, action: 'delete' })}
                            className="rounded-lg p-1.5 text-slate-500 transition hover:bg-red-500/10 hover:text-red-400"
                            aria-label={`Delete ${user.fullName}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination {...data} onPageChange={setPage} />
          </>
        ) : (
          <EmptyState
            icon={<Users className="h-6 w-6" />}
            title="No users yet"
            description={search || status ? 'Try widening your filters.' : 'Add your first user to get started.'}
          />
        )}
      </Card>

      <Modal
        open={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        title={
          confirm?.action === 'delete'
            ? 'Remove this user?'
            : confirm?.user.status === 'ON_HOLD'
              ? 'Release this user?'
              : 'Put this user on hold?'
        }
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant={confirm?.action === 'delete' ? 'danger' : 'primary'}
              onClick={act}
              loading={deleteUser.isPending || toggleHold.isPending}
            >
              {confirm?.action === 'delete'
                ? 'Remove user'
                : confirm?.user.status === 'ON_HOLD'
                  ? 'Release'
                  : 'Put on hold'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-400">
          <span className="font-medium text-slate-200">{confirm?.user.fullName}</span>{' '}
          {confirm?.action === 'delete' ? (
            <>
              will lose access immediately and all their mailboxes will stop syncing. Their collected results are kept
              for reporting.
            </>
          ) : confirm?.user.status === 'ON_HOLD' ? (
            <>will be able to sign in again, and their mailboxes will resume checking for new mail.</>
          ) : (
            <>
              will be signed out and blocked from signing in. All their mailboxes stop being checked, and no links are
              visited, until you release them.
            </>
          )}
        </p>
      </Modal>
    </div>
  );
}
